/**
 * Small, pure helpers shared by the Team Chat components: names and avatar
 * tones, times, file kinds (and which of them the in-app file viewer can
 * open), and recognising links back into Lodgeic so they can open in place.
 */
import { NAV_MAIN, NAV_FOOTER, NAV_SETTINGS, findTrail } from '../../../config/navConfig';

/* ── People ─────────────────────────────────────────────── */

export const initialsOf = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0] || '?').substring(0, 2).toUpperCase();
};

/** Stable 0–4 tone index for an id; the tones are defined in Chat.module.css. */
export const toneOf = (id) => {
  const hash = String(id || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return hash % 5;
};

export const firstName = (user) => String(user?.name || user?.id || '').split(' ')[0];

/** Conversation title: the other person's name in a DM, else the group's name or first names. */
export const channelDisplayName = (channel, meId) => {
  const members = Object.values(channel?.state?.members || {});
  const others = members.filter((m) => m.user?.id !== meId);
  if (others.length === 1) return others[0].user?.name || others[0].user?.id || 'Unknown user';
  if (channel?.data?.name && !/^(Chat with|Group chat with) /.test(channel.data.name)) return channel.data.name;
  if (others.length > 1) {
    const names = others.map((m) => String(m.user?.name || m.user?.id || '').split(' ')[0]);
    if (names.length <= 3) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }
  return channel?.data?.name || 'New conversation';
};

/* ── Time ───────────────────────────────────────────────── */

export const formatClock = (ts) => (ts
  ? new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  : '');

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

/** "Today", "Yesterday", "Tuesday, October 21" (+ year when not this year). */
export const formatDayLabel = (ts) => {
  const day = startOfDay(ts).getTime();
  const today = startOfDay(new Date()).getTime();
  if (day === today) return 'Today';
  if (day === today - 86400000) return 'Yesterday';
  const opts = { weekday: 'long', month: 'long', day: 'numeric' };
  if (new Date(ts).getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return new Date(ts).toLocaleDateString('en-US', opts);
};

/** "9:41" — the hover stamp beside a grouped message. */
export const formatShortClock = (ts) => formatClock(ts).replace(/\s?[AP]M$/i, '');

/** Conversation-list stamp: "9:41 AM", "Yesterday", "Mon", "Oct 20". */
export const formatListStamp = (ts) => {
  if (!ts) return '';
  const day = startOfDay(ts).getTime();
  const today = startOfDay(new Date()).getTime();
  if (day === today) return formatClock(ts);
  if (day === today - 86400000) return 'Yesterday';
  if (today - day < 6 * 86400000) return new Date(ts).toLocaleDateString('en-US', { weekday: 'short' });
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** "Today 9:34 AM", "Oct 20 4:12 PM" — for file metadata. */
export const formatStamp = (ts) => {
  if (!ts) return '';
  const label = formatDayLabel(ts);
  const short = ['Today', 'Yesterday'].includes(label)
    ? label
    : new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${short} ${formatClock(ts)}`;
};

/* ── Files ──────────────────────────────────────────────── */

export const formatFileSize = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const EXT_KIND = {
  pdf: { label: 'PDF', viewer: 'pdf', tone: 'red' },
  xlsx: { label: 'Excel', viewer: 'excel', tone: 'green' },
  xls: { label: 'Excel', viewer: 'excel', tone: 'green' },
  xlsm: { label: 'Excel', viewer: 'excel', tone: 'green' },
  csv: { label: 'CSV', viewer: 'csv', tone: 'green' },
  json: { label: 'JSON', viewer: 'json', tone: 'neutral' },
  txt: { label: 'Text', viewer: 'text', tone: 'neutral' },
  log: { label: 'Text', viewer: 'text', tone: 'neutral' },
  md: { label: 'Text', viewer: 'text', tone: 'neutral' },
  png: { label: 'PNG', viewer: 'image', tone: 'neutral' },
  jpg: { label: 'JPG', viewer: 'image', tone: 'neutral' },
  jpeg: { label: 'JPG', viewer: 'image', tone: 'neutral' },
  gif: { label: 'GIF', viewer: 'image', tone: 'neutral' },
  webp: { label: 'WebP', viewer: 'image', tone: 'neutral' },
  heic: { label: 'HEIC', viewer: null, tone: 'neutral' },
  doc: { label: 'Word', viewer: null, tone: 'blue' },
  docx: { label: 'Word', viewer: null, tone: 'blue' },
  ppt: { label: 'PowerPoint', viewer: null, tone: 'red' },
  pptx: { label: 'PowerPoint', viewer: null, tone: 'red' },
  zip: { label: 'ZIP', viewer: null, tone: 'neutral' },
  mov: { label: 'Video', viewer: null, tone: 'neutral' },
  mp4: { label: 'Video', viewer: null, tone: 'neutral' },
};

const MIME_EXT = {
  'application/pdf': 'pdf',
  'text/csv': 'csv',
  'application/json': 'json',
  'text/plain': 'txt',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

export const extensionOf = (name, mime) => {
  const fromName = String(name || '').split('?')[0].split('.').pop();
  const ext = fromName && fromName !== name ? fromName.toLowerCase() : '';
  return ext || MIME_EXT[String(mime || '').toLowerCase()] || '';
};

/** Share cards rendered by their own components, never as plain attachments. */
export const SHARE_TYPES = ['pbi_filter_share', 'ai_session_share', 'native_report_share'];

/**
 * Normalise one Stream attachment.
 *   kind: 'image' | 'file' | 'link' | 'share'
 *   viewerType: what FileViewerModal should render it as, or null when only a download makes sense
 */
export const describeAttachment = (att = {}) => {
  if (SHARE_TYPES.includes(att.type)) return { kind: 'share', att };

  const isScraped = Boolean(att.og_scrape_url);
  const url = att.asset_url || att.image_url || att.thumb_url || att.title_link || att.og_scrape_url || '';
  const name = att.title || att.name || att.fallback || (url ? decodeURIComponent(url.split('?')[0].split('/').pop()) : 'Attachment');
  const ext = extensionOf(att.title || att.name || url, att.mime_type);
  const meta = EXT_KIND[ext] || { label: ext ? ext.toUpperCase() : 'File', viewer: null, tone: 'neutral' };

  // Stream marks every unfurl that carries a picture as type 'image', pages
  // included. Only a link that IS an image (nothing but the picture came back)
  // shows as the image itself; everything else is a link card.
  const isDirectImage = isScraped && !att.title && !att.text
    && (att.image_url === att.og_scrape_url || att.asset_url === att.og_scrape_url || !att.title_link);
  if (isScraped && !isDirectImage) {
    return {
      kind: 'link',
      url: att.title_link || att.og_scrape_url,
      title: att.title || att.og_scrape_url,
      text: att.text || '',
      image: att.image_url || att.thumb_url || '',
      site: att.author_name || hostOf(att.title_link || att.og_scrape_url),
      host: hostOf(att.title_link || att.og_scrape_url),
      att,
    };
  }

  const isImage = att.type === 'image' || String(att.mime_type || '').startsWith('image/') || meta.viewer === 'image';
  if (isImage) {
    return {
      kind: 'image',
      url: att.image_url || att.asset_url || att.thumb_url || att.og_scrape_url,
      thumb: att.thumb_url || att.image_url || att.asset_url,
      name,
      ext: (ext || 'image').toUpperCase(),
      size: att.file_size,
      width: att.original_width,
      height: att.original_height,
      viewerType: 'image',
      att,
    };
  }

  return {
    kind: 'file',
    url,
    name,
    ext: (ext || 'file').toUpperCase(),
    typeLabel: meta.label,
    tone: meta.tone,
    size: att.file_size,
    viewerType: url ? meta.viewer : null,
    att,
  };
};

/** Every image and viewable file in a list of messages, oldest first — the viewer's gallery. */
export const collectViewerItems = (messages = []) => {
  const items = [];
  messages.forEach((message) => {
    if (message.type === 'deleted' || message.deleted_at) return;
    (message.attachments || []).forEach((att, index) => {
      const d = describeAttachment(att);
      if ((d.kind === 'image' || d.kind === 'file') && d.viewerType && d.url) {
        items.push({
          key: `${message.id}:${index}`,
          messageId: message.id,
          url: d.url,
          thumb: d.kind === 'image' ? d.thumb : null,
          fileType: d.viewerType,
          fileName: d.name,
          ext: d.ext,
          tone: d.tone || 'neutral',
          sender: message.user?.name || message.user?.id || '',
          createdAt: message.created_at,
          size: d.size,
        });
      }
    });
  });
  return items;
};

/* ── Links ──────────────────────────────────────────────── */

export const hostOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch (_) { return ''; }
};

const URL_RE = /\bhttps?:\/\/[^\s<>()]+[^\s<>().,;:!?'"’”)]/gi;

/** Split message text into text and link pieces (no HTML is ever injected). */
export const linkifyParts = (text) => {
  const parts = [];
  const value = String(text || '');
  let last = 0;
  value.replace(URL_RE, (match, offset) => {
    if (offset > last) parts.push({ type: 'text', value: value.slice(last, offset) });
    parts.push({ type: 'link', value: match });
    last = offset + match.length;
    return match;
  });
  if (last < value.length) parts.push({ type: 'text', value: value.slice(last) });
  return parts;
};

export const firstUrlIn = (text) => {
  const m = String(text || '').match(URL_RE);
  return m ? m[0] : null;
};

const FULL_NAV = { main: NAV_MAIN, footer: NAV_FOOTER, settings: NAV_SETTINGS };

const isLodgeicHost = (host) => {
  if (!host) return false;
  if (typeof window !== 'undefined' && host === window.location.hostname) return true;
  return /(^|\.)lodgeic\.io$/i.test(host) && !/^(api|p|reports)\./i.test(host);
};

/**
 * A link into the Lodgeic app → { path, title, section } for an in-place
 * card, or null for any other link.
 */
export const describeLodgeicLink = (url) => {
  let parsed;
  try { parsed = new URL(url); } catch (_) { return null; }
  if (!isLodgeicHost(parsed.hostname)) return null;
  const trail = findTrail(FULL_NAV, parsed.pathname);
  if (!trail) return null;
  const leaf = trail.child || trail.item;
  const title = trail.child && trail.item?.title && trail.item.title !== leaf.title
    ? `${trail.item.title} · ${leaf.title}`
    : leaf.title;
  return {
    path: `${parsed.pathname}${parsed.search}${parsed.hash}`,
    title,
    section: trail.group || 'Lodgeic',
  };
};
